import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  Alert,
  Grid,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import { AdminPanelSettings, Warning } from "@mui/icons-material";
import { USER_ROLES, isAdmin as checkAdmin } from "../../utils/roleUtils";

const EditUserDialog = ({
  open,
  onClose,
  onUpdateUser,
  user,
  currentUserRole,
  plans = [],
}) => {
  const [editedUser, setEditedUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "USER",
    isActive: true,
    planId: "",
  });
  const [errors, setErrors] = useState({});
  const [roleChangeWarning, setRoleChangeWarning] = useState(false);

  useEffect(() => {
    if (open && user) {
      setEditedUser({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        role: user.role || "USER",
        isActive: user.isActive ?? true,
        planId: user.planId || "",
      });
      setErrors({});
      setRoleChangeWarning(false);
    }
  }, [open, user]);

  const validateForm = () => {
    const newErrors = {};

    if (!editedUser.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!editedUser.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!editedUser.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(editedUser.email)) {
      newErrors.email = "Email is invalid";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const updates = {
        firstName: editedUser.firstName.trim(),
        lastName: editedUser.lastName.trim(),
        email: editedUser.email.trim().toLowerCase(),
        role: editedUser.role,
        isActive: editedUser.isActive,
        planId: editedUser.planId || null,
      };
      onUpdateUser(user.id, updates);
    }
  };

  const handleChange = (field, value) => {
    setEditedUser({ ...editedUser, [field]: value });

    // Show warning if role is changing to/from admin
    if (field === "role") {
      const isAdminRole = value === USER_ROLES.ADMIN;
      const wasAdminRole = user?.role === USER_ROLES.ADMIN;
      setRoleChangeWarning(isAdminRole || wasAdminRole);
    }

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const isCurrentUserAdmin = checkAdmin({ role: currentUserRole });
  const isEditingAdmin = user?.role === USER_ROLES.ADMIN;
  const canChangeRole = isCurrentUserAdmin;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Edit User
          </Typography>
          {isEditingAdmin && (
            <Chip
              icon={<AdminPanelSettings />}
              label={user.role}
              color="primary"
              size="small"
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          Update user information
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={editedUser.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                error={Boolean(errors.firstName)}
                helperText={errors.firstName}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={editedUser.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={editedUser.email}
                onChange={(e) => handleChange("email", e.target.value)}
                error={Boolean(errors.email)}
                helperText={errors.email}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth disabled={!canChangeRole}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editedUser.role}
                  label="Role"
                  onChange={(e) => handleChange("role", e.target.value)}
                >
                  <MenuItem value="USER">User</MenuItem>
                  <MenuItem value="ADMIN">Admin</MenuItem>
                </Select>
              </FormControl>
              {!canChangeRole && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  Only Admins can change user roles
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth disabled={!isCurrentUserAdmin}>
                <InputLabel>Plan</InputLabel>
                <Select
                  value={editedUser.planId}
                  label="Plan"
                  onChange={(e) => handleChange("planId", e.target.value)}
                >
                  <MenuItem value="">No Plan</MenuItem>
                  {plans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.name} — {plan.maxSites} sites, {plan.maxStorageGB}GB
                      storage
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!isCurrentUserAdmin && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  Only Admins can change user plans
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editedUser.isActive}
                    onChange={(e) => handleChange("isActive", e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      {editedUser.isActive ? "Active" : "Inactive"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {editedUser.isActive
                        ? "User can access the system"
                        : "User cannot access the system"}
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>

          {roleChangeWarning && (
            <Alert severity="warning" icon={<Warning />} sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                Role Change Warning
              </Typography>
              <Typography variant="caption">
                Changing admin roles affects system access and permissions. This
                action should be performed carefully.
              </Typography>
            </Alert>
          )}

          {!isCurrentUserAdmin && isEditingAdmin && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Only Admins can modify other admin accounts
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Update User
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
